import { Controller } from 'stimulus'
import * as d3 from 'd3'
import {
  createChartFrame,
  drawHorizontalLine,
  drawXTickLabel,
  drawYTickLabel,
  SERIES_COLORS,
} from '../charts/vitals-chart-scaffold'

interface BloodPressureReading {
  label: string
  systolic: number
  diastolic: number
}

interface BloodPressureChartPayload {
  readings: BloodPressureReading[]
  reference_value: number
  y_min: number
  y_max: number
  y_tick_step: number
}

const BAR_COLOR = SERIES_COLORS.mint
const BAR_WIDTH = 30
const BAR_GAP = 25
const BAR_RADIUS = 4

export default class extends Controller {
  static values = { payload: Object }

  declare payloadValue: BloodPressureChartPayload

  private boundRender = this.render.bind(this)

  connect(): void {
    this.render()
    window.addEventListener('resize', this.boundRender)
  }

  disconnect(): void {
    window.removeEventListener('resize', this.boundRender)
  }

  private render(): void {
    const payload = this.payloadValue
    if (!payload.readings?.length) return

    this.element.innerHTML = ''
    const frame = createChartFrame(this.element)

    const yScale = d3
      .scaleLinear()
      .domain([payload.y_min, payload.y_max])
      .range([frame.innerHeight, 0])

    for (
      let tick = payload.y_min;
      tick <= payload.y_max;
      tick += payload.y_tick_step
    ) {
      drawYTickLabel(frame, yScale(tick), tick.toString())
    }
    drawHorizontalLine(frame, frame.innerHeight)
    drawHorizontalLine(frame, yScale(payload.reference_value), { dashed: true })

    const slotWidth = Math.min(
      BAR_WIDTH + BAR_GAP,
      frame.innerWidth / payload.readings.length,
    )
    const barWidth = Math.min(BAR_WIDTH, slotWidth - 4)

    payload.readings.forEach((reading, index) => {
      const x = index * slotWidth
      const top = yScale(reading.systolic)
      frame.chart
        .append('rect')
        .attr('x', x)
        .attr('y', top)
        .attr('width', barWidth)
        .attr(
          'height',
          Math.max(yScale(reading.diastolic) - top, 2 * BAR_RADIUS),
        )
        .attr('rx', BAR_RADIUS)
        .style('fill', BAR_COLOR)
        .append('title')
        .text(`${reading.label}: ${reading.systolic}/${reading.diastolic}`)
      drawXTickLabel(frame, x + barWidth / 2, 'middle', reading.label)
    })
  }
}
